import { envPath, type RealtimeView } from '../api';
import { ago, Empty, ErrorState, num, PageHeader, Spinner, StatTile } from '../components/ui';
import { FeatureHint } from '../components/FeatureHint';
import { useResource } from '../hooks';
import { formatDuration, useT } from '../i18n';
import { useShell } from './ProjectShell';
import { CLI } from '../cliCommand';
import { projectEyebrow } from '../projectNav';

// 稼働ルームは「今」を見る画面なので 5 秒ごとに読み直す（タブが裏にある間は止める）
const POLL_MS = 5000;

const dur = formatDuration;

export function MultiplayerPage() {
  const { project, env } = useShell();
  const r = useResource<RealtimeView>(`${envPath(project.id, env)}/realtime`, POLL_MS);
  const { t, tr } = useT();
  const modes = r.data ? Object.entries(r.data.live.modes) : [];

  return (
    <>
      <PageHeader eyebrow={projectEyebrow(project)} title={t('mp.title')}>
        {r.data && (
          <span className="live-dot" role="status">
            <span aria-hidden="true" />
            {t('mp.live')}
          </span>
        )}
      </PageHeader>
      {/* 既定 OFF: OFF のままだと部屋が 1 つもできない（ゲームからの接続は 403）。空の表示だけでは理由が分からない */}
      <FeatureHint projectId={project.id} env={env} need={['multiplayer']} />
      {r.loading && !r.data && <Spinner />}
      {r.error && <ErrorState error={r.error} what={t('mp.title')} onRetry={r.reload} />}
      {r.data && (
        <>
          <section className="tiles">
            <StatTile label={t('mp.liveRooms')} value={num(r.data.live.rooms)} tone="accent" />
            <StatTile label={t('mp.playersInRooms')} value={num(r.data.live.players)} />
            <StatTile label={t('mp.inMatchmaking')} value={num(r.data.live.queued)} />
            <StatTile label={t('mp.rooms24h')} value={num(r.data.last24h.rooms)} hint={t('mp.avg', { dur: dur(r.data.last24h.avgDurationSeconds) })} />
          </section>

          <section className="card">
            <h2 className="card-title">{t('mp.byMode')}</h2>
            {modes.length === 0 ? (
              <Empty title={t('mp.noRoomsTitle')}>
                <p>{tr('mp.noRoomsBody', { key: <code>multiplayer.modes</code>, call: <code>kumo.rooms.quickMatch('duel')</code> })}</p>
                <p className="small">{tr('mp.noRoomsStarter', { cmd: <code>{CLI} create my-game -t multiplayer-starter</code> })}</p>
              </Empty>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">{t('mp.col.mode')}</th>
                      <th scope="col">{t('mp.col.rooms')}</th>
                      <th scope="col">{t('mp.col.players')}</th>
                      <th scope="col">{t('mp.col.queued')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modes.map(([m, s]) => (
                      <tr key={m}>
                        <td className="mono">{m}</td>
                        <td>{num(s.rooms)}</td>
                        <td>{num(s.players)}</td>
                        <td>{num(s.queued)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="card">
            <h2 className="card-title">{t('mp.recent')}</h2>
            {r.data.recent.length === 0 ? (
              <p className="muted small">{t('mp.noRecent')}</p>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">{t('mp.col.mode')}</th>
                      <th scope="col">{t('mp.col.ended')}</th>
                      <th scope="col">{t('mp.col.length')}</th>
                      <th scope="col">{t('mp.col.peak')}</th>
                      <th scope="col" className="hide-sm">{t('mp.col.how')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.data.recent.map((room) => (
                      <tr key={room.id}>
                        <td>
                          <span className="mono">{room.mode}</span> {room.private && <span className="pill">{t('mp.private')}</span>}
                        </td>
                        <td>{ago(room.endedAt)}</td>
                        <td>{dur(room.durationSeconds)}</td>
                        <td>
                          {num(room.peakPlayers)} / {num(room.totalPlayers)}
                        </td>
                        <td className="hide-sm small muted">
                          {room.origin} · {room.endReason}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </>
  );
}
